import {
  type Account,
  type ClientCommunicationProfile,
  type DetailedNotificationProfile,
  type NotificationProfile,
  type SettingsCopy,
  type User,
  type UserCommunicationProfile,
  AccountNotificationTypes,
  BudgetNotificationTypes,
  InsightNotificationTypes,
} from '../types'

export const PARENT_ENTITY = 'PARENT-ENTITY'

const getProfileLabel = (notificationType: number, copy: SettingsCopy) => {
  const labelMap: Record<number, string> = {
    [BudgetNotificationTypes.BudgetExceeded]: copy.notification_budget_exceeded,
    [BudgetNotificationTypes.BudgetOffTarget]: copy.notification_budget_off_target,
    [AccountNotificationTypes.DebtPaymentReminder]: copy.notification_debt_payment_reminder,
    [AccountNotificationTypes.AccountBalanceLow]: copy.notification_low_balance,
    [AccountNotificationTypes.AccountDepositLargePosted]: copy.notification_large_deposit,
    [AccountNotificationTypes.AccountDepositLarge]: copy.notification_large_deposit,
    [AccountNotificationTypes.TransactionExpenseLarge]: copy.notification_large_expense,
    [AccountNotificationTypes.TransactionFeeCharge]: copy.notification_transaction_fee,
    [AccountNotificationTypes.InternationalTransaction]:
      copy.notification_transaction_is_international,
    [InsightNotificationTypes.AccountBalanceLow]: copy.account_balance_low,
    [InsightNotificationTypes.AccountDepositHigh]: copy.notification_large_deposit,
    [InsightNotificationTypes.BillAmountNotStandard]: copy.bill_amount_not_standard,
    [InsightNotificationTypes.BudgetExceeded]: copy.notification_budget_exceeded,
    [InsightNotificationTypes.BudgetOffTarget]: copy.notification_budget_off_target,
    [InsightNotificationTypes.CategoryBudget]: copy.category_budget,
    [InsightNotificationTypes.CategorySpending]: copy.category_spending,
    [InsightNotificationTypes.CreditCardCloseToLimit]: copy.credit_card_close_to_limit,
    [InsightNotificationTypes.DebtPaymentReminder]: copy.debt_payment_reminder,
    [InsightNotificationTypes.DebtPaymentReminderV3]: copy.debt_payment_reminder_v3,
    [InsightNotificationTypes.DuplicatePayment]: copy.duplicate_payment,
    [InsightNotificationTypes.GoalProgress]: copy.goal_progress,
    [InsightNotificationTypes.MonthlySpendComparison]: copy.monthly_spend_comparison,
    [InsightNotificationTypes.MonthlySpendToIncomeComparison]:
      copy.monthly_spend_to_income_comparison,
    [InsightNotificationTypes.MonthlySubscriptionAggregate]: copy.monthly_subscription_aggregate,
    [InsightNotificationTypes.MonthlyTopMerchantSpending]: copy.monthly_top_merchant_spending,
    [InsightNotificationTypes.SaveAnExtra100Dollars]: copy.save_an_extra_100_dollars,
    [InsightNotificationTypes.SavingsOpportunity]: copy.savings_opportunity,
    [InsightNotificationTypes.SetUpDirectDeposit]: copy.set_up_direct_deposit,
    [InsightNotificationTypes.SpendingPlanBudgetExceeded]: copy.spending_plan_budget_exceeded,
    [InsightNotificationTypes.SpendingPlanCategorySpending]: copy.spending_plan_category_spending,
    [InsightNotificationTypes.SwitchDirectDeposit]: copy.switch_direct_deposit,
    [InsightNotificationTypes.TransactionFeeCharge]: copy.notification_transaction_fee,
    [InsightNotificationTypes.TransactionIsInternational]:
      copy.notification_transaction_is_international,
    [InsightNotificationTypes.UncategorizedTransactions]: copy.uncategorized_transactions,
    [InsightNotificationTypes.UnifiedDeposit]: copy.unified_deposit,
    [InsightNotificationTypes.UpcomingBill]: copy.upcoming_bill,
    [InsightNotificationTypes.WeekendSpendingSummary]: copy.weekend_spending_summary,
    [InsightNotificationTypes.WeeklyNewMerchants]: copy.weekly_new_merchants,
    [InsightNotificationTypes.DiscoveredAccounts]: copy.discovered_account,
    [InsightNotificationTypes.TransactionExpenseLarge]: copy.large_transaction,
    [InsightNotificationTypes.PromotionalCampaignMessages]: copy.promotional_campaign_messages,
    [InsightNotificationTypes.TransparentOverdraft]: copy.transparent_overdraft,
  }

  return labelMap[notificationType] || ''
}

export const augmentNotificationProfiles = (
  accounts: Account[],
  clientCommunicationsProfile: ClientCommunicationProfile,
  copy: SettingsCopy,
  profiles: NotificationProfile[],
  userCommunicationsProfile: UserCommunicationProfile,
  user: User,
) => {
  const detailedProfiles = [] as DetailedNotificationProfile[]
  const {
    email_notifications_enabled: clientEmailEnabled,
    sms_notifications_enabled: clientSmsEnabled,
    push_notifications_enabled: clientPushEnabled,
  } = clientCommunicationsProfile

  const {
    opted_out_of_email_notifications: userEmailOptedOut,
    opted_out_of_sms_notifications: userSmsOptedOut,
    opted_out_of_push_notifications: userPushOptedOut,
  } = userCommunicationsProfile

  for (const profile of profiles.filter((profile) => profile.entity_guid === PARENT_ENTITY)) {
    const label = getProfileLabel(profile.notification_type, copy)
    const children = profiles.filter(
      (child) =>
        child.entity_guid !== PARENT_ENTITY &&
        child.notification_type === profile.notification_type &&
        accounts.some((account) => account.guid === child.entity_guid),
    )

    const deliveryMethods = [] as string[]
    if (clientEmailEnabled && !userEmailOptedOut && profile.email_channel && user.email) {
      deliveryMethods.push(copy.delivery_method_email_label)
    }
    if (clientSmsEnabled && !userSmsOptedOut && profile.sms_channel && user.phone) {
      deliveryMethods.push(copy.delivery_method_sms_label)
    }
    if (clientPushEnabled && !userPushOptedOut && profile.push_channel) {
      deliveryMethods.push(copy.delivery_method_push_label)
    }

    detailedProfiles.push({
      ...profile,
      children: children.map((child) => ({
        ...child,
        account: accounts.find((a) => a.guid === child.entity_guid),
        label,
      })),
      deliveryMethodsLabel: deliveryMethods.join(', '),
      label,
    })
  }

  return detailedProfiles
}
